import { parseISO } from 'date-fns';
import { format } from 'date-fns/format';
import type { ReactNode } from 'react';

import { MediaLivestreamStatus } from '#components/Media/LiveStreamStatus';
import { MediaLivestream } from '#components/Media/MediaLivestream';
import { useEnvironment } from '#cutils/context/EnvironmentContext';
import { logger } from '#utils/logging/logger';

import { IMAGE_SIZE_ONE_THIRD } from '../../BoardImageSizes';

import styles from './BoardSectionItemsBR24Rundschau.module.scss';
import { useRundschauLivestream } from './use-rundschau-livestream';

/**
 * Milliseconds for which the prelive state should be shown
 */
const PRE_LIVE_IN_MS = 5 * 60 * 1000;

export function BoardSectionItemBR24Rundschau() {
	const { images } = useEnvironment();
	const { livestreamStatus, loading } = useRundschauLivestream(PRE_LIVE_IN_MS);

	if (livestreamStatus === null) {
		// Only log error if we're not loading and still have no data
		if (!loading) {
			logger.error('Rundschau is not available');
		}
		return null;
	}

	const teaser = livestreamStatus.defaultTeaserImage;
	const thumbnailUrl = teaser?.url || images.DEFAULT_RUNDSCHAU_IMAGE;

	const titleContent = (
		<div className={styles.title}>
			<div className="heading3">BR24 im TV</div>
		</div>
	);

	let playButtonTitle: string;

	let customPosterImageTitle: ReactNode;
	if (livestreamStatus.status === 'PAST') {
		const formattedStartDate = livestreamStatus.startDate
			? format(parseISO(livestreamStatus.startDate.toISOString()), "dd.MM.yyyy, HH:mm 'Uhr'")
			: null;

		playButtonTitle = `BR24 im TV Sendung vom ${formattedStartDate} abspielen`;

		customPosterImageTitle = (
			<>
				{titleContent}
				<div className={`${styles.status} body7`}>
					Sendung vom:
					<br />
					{formattedStartDate}
				</div>
			</>
		);
	} else {
		playButtonTitle = `BR24 Livestream abspielen`;
		customPosterImageTitle = (
			<>
				{titleContent}
				<MediaLivestreamStatus startDate={livestreamStatus.startDate} endDate={livestreamStatus.endDate} hasRecordings={false} />
			</>
		);
	}

	return (
		<MediaLivestream
			copyright=""
			livestreamEndDate={livestreamStatus.endDate?.toISOString() ?? null}
			livestreamStartDate={livestreamStatus.startDate?.toISOString() ?? null}
			liveStreamHlsUrl={livestreamStatus.status !== 'PAST' ? livestreamStatus.liveStreamUrl : null}
			thumbnailUrl={thumbnailUrl}
			thumbnailAltText={teaser?.title ?? null}
			thumbnailCopyright={teaser?.copyright ?? null}
			thumbnailLink={null}
			videos={livestreamStatus.status === 'PAST' ? livestreamStatus.onDemandVideos : []}
			title="BR24 im TV"
			playButtonTitle={playButtonTitle}
			publicationDate={livestreamStatus.modifiedAt?.toISOString()}
			sizes={IMAGE_SIZE_ONE_THIRD}
			posterImageTitle={customPosterImageTitle}
			// special module id for tracking purposes
			moduleId="rundschau"
			preliveDuration={PRE_LIVE_IN_MS}
		/>
	);
}
